import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SavedChart } from '../../../types/chart.js'
import { listCharts } from '../../../services/chartService.js'
import { AppDialog } from '../AppDialog.js'
import { Button } from '../Button.js'
import { TextInput } from '../TextInput.js'
import { ChartBuilderDialog } from './ChartBuilderDialog.js'
import { useNls } from '../../../nls/nlsProvider.js'
import './chart.css'

export interface ChartLibraryPickerProps {
  open: boolean
  workspaceId: string
  onClose: () => void
  onSelect: (chartId: string) => void
  onNewChart?: () => void
  title?: string
  testId?: string
}

export function ChartLibraryPicker({
  open,
  workspaceId,
  onClose,
  onSelect,
  onNewChart,
  title,
  testId = 'chart-library-picker',
}: ChartLibraryPickerProps) {
  const { t } = useNls()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [charts, setCharts] = useState<SavedChart[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedChartId, setSelectedChartId] = useState<string>('')
  const [builderOpen, setBuilderOpen] = useState(false)

  const fetchCharts = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    try {
      const result = await listCharts(workspaceId)
      setCharts(result)
      setSelectedChartId((prev) => prev || result[0]?.id || '')
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      setCharts([])
    } finally {
      setLoading(false)
    }
  }, [workspaceId])

  useEffect(() => {
    if (!open || !workspaceId) return
    let isCurrent = true
    listCharts(workspaceId)
      .then((result) => {
        if (!isCurrent) return
        setCharts(result)
        setSelectedChartId((prev) => prev || result[0]?.id || '')
        setError(null)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (!isCurrent) return
        setError(err instanceof Error ? err : new Error(String(err)))
        setCharts([])
        setLoading(false)
      })
    return () => {
      isCurrent = false
    }
  }, [open, workspaceId])

  const filteredCharts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return charts
    return charts.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.type.toLowerCase().includes(q) ||
        c.dataset.toLowerCase().includes(q)
    )
  }, [charts, searchQuery])

  const handleOpenBuilder = () => {
    if (onNewChart) {
      onNewChart()
    } else {
      setBuilderOpen(true)
    }
  }

  const handleBuilderSaved = (newChartId: string) => {
    setBuilderOpen(false)
    onSelect(newChartId)
  }

  return (
    <>
      <AppDialog
        open={open}
        title={title || t('NLSDesignChartSelectTitle')}
        onDismiss={onClose}
        size="md"
        data-testid={testId}
        actions={[
          {
            label: t('NLSDesignChartNew'),
            variant: 'secondary',
            onClick: handleOpenBuilder,
          },
          {
            label: t('NLSChartLibraryPickerPlace'),
            variant: 'primary',
            disabled: !selectedChartId || loading || Boolean(error),
            onClick: () => {
              if (selectedChartId) onSelect(selectedChartId)
            },
          },
        ]}
      >
        <div className="chartLibraryPickerContent">
          <TextInput
            type="search"
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('NLSChartLibraryPickerSearchPlaceholder')}
            aria-label={t('NLSChartLibraryPickerSearchPlaceholder')}
            block
          />

          {loading ? (
            <div className="chartLibraryPickerLoading">
              <span>Loading charts…</span>
            </div>
          ) : error ? (
            <div className="chartLibraryPickerError">
              <p>{t('NLSChartLibraryPickerError')}</p>
              <Button size="sm" variant="secondary" onClick={() => void fetchCharts()}>
                {t('NLSChartLibraryPickerRetry')}
              </Button>
            </div>
          ) : charts.length === 0 ? (
            <div className="chartLibraryPickerEmpty">
              <p>{t('NLSChartLibraryPickerEmpty')}</p>
            </div>
          ) : filteredCharts.length === 0 ? (
            <div className="chartLibraryPickerEmpty">
              <p>No matching charts found.</p>
            </div>
          ) : (
            <ul className="chartLibraryPickerList" role="radiogroup" aria-label={t('NLSDesignChartSelectTitle')}>
              {filteredCharts.map((c) => {
                const isSelected = c.id === selectedChartId
                return (
                  <li
                    key={c.id}
                    className={`chartLibraryPickerItem ${isSelected ? 'is-selected' : ''}`}
                    role="radio"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onClick={() => setSelectedChartId(c.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setSelectedChartId(c.id)
                      }
                    }}
                  >
                    <span className="chartLibraryPickerItemTitle">{c.title}</span>
                    <span className="chartLibraryPickerItemMeta">
                      {c.type} · {c.dataset}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </AppDialog>

      {builderOpen && (
        <ChartBuilderDialog
          open={builderOpen}
          workspaceId={workspaceId}
          onClose={() => setBuilderOpen(false)}
          onSaved={handleBuilderSaved}
        />
      )}
    </>
  )
}
